import { useCallback, useEffect, useRef, useState } from "react";

import {
  getAcknowledgementRequestQueryControllerGet,
  listMyAcknowledgementsQueryControllerMyRequests,
} from "@/generated/acknowledgements/acknowledgements";
import type { MyAcknowledgementRequestDto } from "@/generated/model";
import { offlineDb } from "@/lib/offline/db";
import { MAX_RETRIES, syncService } from "@/lib/offline/syncService";

import { myAcknowledgementsCacheKey } from "./use-offline-acknowledgement-sign";

export interface AcknowledgementRequestWithSync extends MyAcknowledgementRequestDto {
  localSyncStatus?: {
    /** Signed on this device but not yet accepted by the server. */
    isLocallySigned: boolean;
    pendingCount: number;
    failedCount: number;
  };
  /** True when this request's document is cached, so it can be read offline. */
  hasCachedDocument?: boolean;
}

interface UseOfflineMyAcknowledgementsResult {
  items: AcknowledgementRequestWithSync[];
  isLoading: boolean;
  isOffline: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * The staff member's own outstanding acknowledgements, offline-first (AC10/AC35).
 *
 * Follows `use-offline-follow-up-actions`: cached rows immediately, fresh fetch
 * if online, local pending state merged in, cache updated.
 *
 * The cache key includes the USER id, not just the nursery. That is not
 * defensive tidiness — shared-device PIN login means two people use one tablet,
 * and a nursery-keyed row would show one staff member's outstanding legal
 * obligations to whoever signs in next.
 */
export function useOfflineMyAcknowledgements(
  nurseryId: string | null,
  userId: string | null,
): UseOfflineMyAcknowledgementsResult {
  const [items, setItems] = useState<AcknowledgementRequestWithSync[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const initializedRef = useRef(false);
  const scopeRef = useRef(`${nurseryId}:${userId}`);
  const previousPendingCountRef = useRef(0);

  /**
   * Re-merges queue state onto the rows already on screen, reading the cached
   * list rather than the network — safe to run on every sync tick.
   */
  const refreshLocalState = useCallback(async () => {
    if (!nurseryId || !userId) return;
    const cached = await offlineDb.acknowledgementRequests.get(
      myAcknowledgementsCacheKey(nurseryId, userId),
    );
    if (!cached) return;
    setItems(
      await withLocalState(
        (cached.requests as MyAcknowledgementRequestDto[]) ?? [],
      ),
    );
  }, [nurseryId, userId]);

  const load = useCallback(async () => {
    if (!nurseryId || !userId) {
      setItems([]);
      setIsLoading(false);
      return;
    }
    const cacheKey = myAcknowledgementsCacheKey(nurseryId, userId);

    try {
      const cached = await offlineDb.acknowledgementRequests.get(cacheKey);
      if (cached) {
        setItems(
          await withLocalState(
            (cached.requests as MyAcknowledgementRequestDto[]) ?? [],
          ),
        );
        setIsLoading(false);
      }

      if (!navigator.onLine) {
        setIsOffline(true);
        setIsLoading(false);
        return;
      }
      setIsOffline(false);

      const fresh = await listMyAcknowledgementsQueryControllerMyRequests(
        nurseryId,
        { outstandingOnly: true },
      );
      const requests = fresh.data ?? [];

      await offlineDb.acknowledgementRequests.put({
        id: cacheKey,
        nurseryId,
        userId,
        cachedAt: new Date().toISOString(),
        requests,
      });
      setItems(await withLocalState(requests));
      setError(null);

      // Cache each document while we still have a connection. Without this,
      // AC35's offline signing only ever works for a document the staff member
      // happened to open online first — the reader page is otherwise the only
      // writer of the detail cache. Fire-and-forget: a failed prefetch costs a
      // later read, not this list.
      void prefetchAcknowledgementDocuments(
        requests.map((request) => request.requestId),
      );
    } catch (err) {
      // A cached list is better than an error screen — the obligation is real
      // whether or not the network is.
      setIsOffline(!navigator.onLine);
      if (items.length === 0) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setIsLoading(false);
    }
    // `items` is deliberately excluded: it is only read to decide whether to
    // surface an error, and including it would refetch on every merge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nurseryId, userId]);

  useEffect(() => {
    const scope = `${nurseryId}:${userId}`;
    if (!initializedRef.current || scopeRef.current !== scope) {
      initializedRef.current = true;
      scopeRef.current = scope;
      void load();
    }
  }, [nurseryId, userId, load]);

  // Held in a ref so the sync subscription below does not resubscribe on every
  // load() identity change.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Refresh as the queue drains, so a signature made offline clears the row
  // once it lands.
  //
  // Only refetch on the pending → 0 transition, following
  // use-offline-follow-up-actions. The subscription fires once per queue item,
  // so calling the full load() every time meant a tablet reconnecting with 40
  // queued items fired ~42 redundant GETs (each now also sweeping the document
  // prefetch) — and a mid-drain refetch rewrites the cache with a list that
  // still contains the just-signed request, making the signed row flicker back.
  // In-between ticks only re-merge local state, which is a Dexie read.
  useEffect(() => {
    const unsubscribe = syncService.subscribe((status) => {
      const previousPending = previousPendingCountRef.current;
      if (previousPending > 0 && status.pendingCount === 0) {
        void loadRef.current();
      } else if (status.pendingCount !== previousPending) {
        void refreshLocalState();
      }
      previousPendingCountRef.current = status.pendingCount;
    });
    return () => {
      unsubscribe();
    };
  }, [refreshLocalState]);

  return { items, isLoading, isOffline, error, refetch: load };
}

/**
 * Merges queue state and document availability onto each row.
 *
 * `MAX_RETRIES` is imported rather than repeating the literal 5 that
 * `use-offline-follow-up-actions` currently hardcodes — a drift there would
 * silently mislabel exhausted items as still pending.
 */
async function withLocalState(
  requests: MyAcknowledgementRequestDto[],
): Promise<AcknowledgementRequestWithSync[]> {
  if (requests.length === 0) {
    return [];
  }

  const [queued, acks, cachedDetails] = await Promise.all([
    offlineDb.syncQueue
      .filter(
        (item) =>
          item.type === "acknowledgementSign" ||
          item.type === "acknowledgementRead",
      )
      .toArray(),
    offlineDb.acknowledgementAcks.toArray(),
    offlineDb.acknowledgementRequestDetails.toArray(),
  ]);

  const acksById = new Map(acks.map((ack) => [ack.id, ack]));
  const cachedIds = new Set(cachedDetails.map((detail) => detail.id));

  return requests.map((request) => {
    const mine = queued.filter((item) => item.entityId === request.requestId);
    const failedCount = mine.filter(
      (item) => item.attempts >= MAX_RETRIES,
    ).length;
    return {
      ...request,
      hasCachedDocument: cachedIds.has(request.requestId),
      localSyncStatus: {
        isLocallySigned: Boolean(acksById.get(request.requestId)?.signedAt),
        pendingCount: mine.length - failedCount,
        failedCount,
      },
    };
  });
}

/**
 * Caches the documents behind a set of outstanding requests, so they can be
 * read and signed offline (AC35).
 *
 * Skips anything already cached, and runs a few at a time rather than firing
 * one request per outstanding item at once — the same concurrency shape as
 * prefetchScheduledQuestionnaireDetails.
 */
export async function prefetchAcknowledgementDocuments(
  requestIds: string[],
  concurrency = 3,
): Promise<void> {
  if (!navigator.onLine || requestIds.length === 0) return;

  const missing: string[] = [];
  for (const requestId of requestIds) {
    const cached = await offlineDb.acknowledgementRequestDetails.get(requestId);
    if (!cached) missing.push(requestId);
  }

  for (let i = 0; i < missing.length; i += concurrency) {
    await Promise.all(
      missing.slice(i, i + concurrency).map(async (requestId) => {
        try {
          const detail =
            await getAcknowledgementRequestQueryControllerGet(requestId);
          await offlineDb.acknowledgementRequestDetails.put({
            id: requestId,
            cachedAt: new Date().toISOString(),
            detail: detail.data as unknown,
          });
        } catch (err) {
          console.warn(
            `[Offline] Could not cache acknowledgement ${requestId}:`,
            err,
          );
        }
      }),
    );
  }
}
